<template>
  <div class="ua">
    <!-- KPIs -->
    <div class="ua-kpis">
      <analysis-stat-card
        v-for="kpi in kpis"
        :key="kpi.label"
        v-bind="kpi"
      />
    </div>

    <!-- filters -->
    <div class="ua-filters">
      <div class="ua-filters__row">
        <div class="ua-search">
          <feather-icon
            icon="SearchIcon"
            size="16"
          />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search by engineer name..."
          >
          <button
            v-if="searchQuery"
            type="button"
            class="ua-search__clear"
            aria-label="Clear search"
            @click="searchQuery = ''"
          >
            <feather-icon
              icon="XIcon"
              size="14"
            />
          </button>
        </div>

        <button
          type="button"
          class="ua-toggle"
          :class="{ 'ua-toggle--active': filterLateRequests }"
          @click="filterLateRequests = !filterLateRequests"
        >
          <feather-icon
            icon="AlertTriangleIcon"
            size="15"
          />
          Late requests only
        </button>

        <button
          v-if="hasActiveFilters"
          type="button"
          class="ua-reset"
          @click="resetFilters"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="14"
          />
          Reset
        </button>
      </div>

      <div class="ua-filters__row">
        <span class="ua-filters__label">
          <feather-icon
            icon="FilterIcon"
            size="14"
          />
          Sources
        </span>
        <button
          v-for="source in availableSources"
          :key="source.key"
          type="button"
          class="ua-chip"
          :class="{ 'ua-chip--active': selectedSources.includes(source.key) }"
          :style="{ '--accent': source.theme.color, '--accent-soft': source.soft }"
          @click="toggleSource(source.key)"
        >
          <feather-icon
            :icon="source.theme.icon"
            size="13"
          />
          {{ source.theme.label }}
        </button>
      </div>
    </div>

    <!-- table -->
    <div
      v-if="filteredItems.length"
      class="ua-table-wrap"
    >
      <table class="ua-table">
        <thead>
          <tr>
            <th class="col-index">
              #
            </th>
            <th
              v-for="col in columns"
              :key="col.key"
              :class="['sortable', col.class, { 'is-sorted': sortKey === col.key }]"
              @click="setSort(col.key)"
            >
              <span class="th-inner">
                <feather-icon
                  :icon="col.icon"
                  size="14"
                />
                {{ col.label }}
                <span
                  v-if="sortKey === col.key"
                  class="th-arrow"
                >
                  <feather-icon
                    :icon="sortDir === 'desc' ? 'ArrowDownIcon' : 'ArrowUpIcon'"
                    size="13"
                  />
                </span>
              </span>
            </th>
            <th>
              <span class="th-inner">
                <feather-icon
                  icon="GridIcon"
                  size="14"
                />
                Sources
              </span>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="(item, index) in filteredItems"
            :key="`${item.name}-${index}`"
          >
            <td class="col-index">
              {{ index + 1 }}
            </td>

            <td>
              <div class="ua-user">
                <span
                  class="ua-avatar"
                  :style="{ background: avatarColor(item.name) }"
                >{{ initials(item.name) }}</span>
                <span class="ua-user__name">{{ item.name }}</span>
              </div>
            </td>

            <td class="col-load">
              <div class="ua-load">
                <span class="ua-load__value">{{ format(item.load) }}</span>
                <div class="ua-load__track">
                  <div
                    class="ua-load__bar"
                    :class="`ua-load__bar--${loadLevel(item.load)}`"
                    :style="{ width: loadWidth(item.load) }"
                  />
                </div>
              </div>
            </td>

            <td class="col-num">
              <span class="ua-num ua-num--pending">
                <feather-icon
                  icon="ClockIcon"
                  size="13"
                />
                {{ format(item.pending) }}
              </span>
            </td>

            <td class="col-num">
              <span
                class="ua-num"
                :class="item.late > 0 ? 'ua-num--late' : 'ua-num--zero'"
              >
                <feather-icon
                  icon="AlertTriangleIcon"
                  size="13"
                />
                {{ format(item.late) }}
              </span>
            </td>

            <td>
              <div class="ua-sources">
                <button
                  v-for="(value, key) in item.sources"
                  :key="key"
                  type="button"
                  class="ua-source"
                  :style="sourceStyle(key)"
                  :title="`${themeOf(key).label} - ${format(value.total_count)} requests`"
                  @click="showSourceData(item.name, key, value)"
                >
                  <feather-icon
                    :icon="themeOf(key).icon"
                    size="13"
                  />
                  {{ themeOf(key).label }}
                  <span
                    v-if="toNum(value.late_requests) > 0"
                    class="ua-source__late"
                  />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-else
      class="ua-empty"
    >
      <feather-icon
        icon="UsersIcon"
        size="40"
      />
      <h5>No engineers match your filters</h5>
      <p>Try changing the search or the selected sources.</p>
    </div>

    <p class="ua-footnote">
      Showing {{ filteredItems.length }} of {{ items.length }} engineers
    </p>

    <source-detail-modal
      :visible.sync="isModalVisible"
      :source="sourceData.source"
      :subtitle="sourceData.name"
      :data="sourceData.data"
    />
  </div>
</template>

<script>
import AnalysisStatCard from '@/components/analysis-table/AnalysisStatCard.vue'
import SourceDetailModal from '@/components/analysis-table/SourceDetailModal.vue'
import {
  getSourceTheme, tint, toNumber, formatNumber, palette,
} from '@/components/analysis-table/analysisTheme'

const AVATAR_COLORS = ['#1c84f2', '#0a62e8', '#0fb5c9', '#3a54c4', '#34b3f0', '#6a63f0', '#13a98b', '#4a6fa5']

export default {
  components: {
    AnalysisStatCard,
    SourceDetailModal,
  },
  props: {
    userAnalysisData: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isModalVisible: false,
      sourceData: { name: '', source: '', data: {} },
      searchQuery: '',
      selectedSources: [],
      filterLateRequests: false,
      sortKey: 'load',
      sortDir: 'desc',
      columns: [
        {
          key: 'name', label: 'Engineer', icon: 'UserIcon', class: 'col-name',
        },
        {
          key: 'load', label: 'Load', icon: 'ActivityIcon', class: 'col-load',
        },
        {
          key: 'pending', label: 'Pending', icon: 'ClockIcon', class: 'col-num',
        },
        {
          key: 'late', label: 'Late', icon: 'AlertTriangleIcon', class: 'col-num',
        },
      ],
    }
  },
  computed: {
    items() {
      return (this.userAnalysisData || []).map(data => {
        const sources = data.sources || {}
        return {
          name: data.engineer_name || '',
          load: toNumber(data.total_load),
          pending: toNumber(data.total_pending),
          late: Object.values(sources).reduce((acc, s) => acc + toNumber(s.late_requests), 0),
          sources,
        }
      })
    },
    maxLoad() {
      return Math.max(1, ...this.items.map(i => i.load))
    },
    availableSources() {
      const keys = new Set()
      this.items.forEach(item => Object.keys(item.sources).forEach(k => keys.add(k)))

      return [...keys]
        .map(key => {
          const theme = getSourceTheme(key)
          return { key, theme, soft: tint(theme.color, 0.14) }
        })
        .sort((a, b) => a.theme.label.localeCompare(b.theme.label))
    },
    hasActiveFilters() {
      return Boolean(this.searchQuery || this.selectedSources.length || this.filterLateRequests)
    },
    filteredItems() {
      const query = this.searchQuery.trim().toLowerCase()
      let filtered = this.items.filter(item => item.name.toLowerCase().includes(query))

      if (this.selectedSources.length) {
        filtered = filtered.filter(item => this.selectedSources.some(source => item.sources[source]))
      }

      if (this.filterLateRequests) {
        filtered = filtered.filter(item => item.late > 0)
      }

      const dir = this.sortDir === 'desc' ? -1 : 1
      const key = this.sortKey

      return filtered.sort((a, b) => {
        if (key === 'name') return a.name.localeCompare(b.name) * dir
        return (a[key] - b[key]) * dir
      })
    },
    kpis() {
      const sum = key => this.items.reduce((acc, i) => acc + i[key], 0)

      return [
        {
          label: 'Engineers', value: this.items.length, icon: 'UsersIcon', color: palette.primary,
        },
        {
          label: 'Total Load', value: sum('load'), icon: 'ActivityIcon', color: '#0a62e8', hint: 'requests in turn',
        },
        {
          label: 'Total Pending', value: sum('pending'), icon: 'ClockIcon', color: palette.warning,
        },
        {
          label: 'Late Requests', value: sum('late'), icon: 'AlertTriangleIcon', color: palette.danger,
        },
      ]
    },
  },
  methods: {
    format: formatNumber,
    toNum: toNumber,
    themeOf: getSourceTheme,
    showSourceData(name, key, value) {
      this.sourceData = { name, source: key, data: value }
      this.isModalVisible = true
    },
    toggleSource(key) {
      const index = this.selectedSources.indexOf(key)
      if (index === -1) this.selectedSources.push(key)
      else this.selectedSources.splice(index, 1)
    },
    resetFilters() {
      this.searchQuery = ''
      this.selectedSources = []
      this.filterLateRequests = false
    },
    setSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'desc' ? 'asc' : 'desc'
      } else {
        this.sortKey = key
        this.sortDir = key === 'name' ? 'asc' : 'desc'
      }
    },
    sourceStyle(key) {
      const { color } = getSourceTheme(key)
      return { '--accent': color, '--accent-soft': tint(color, 0.12) }
    },
    initials(name) {
      const parts = String(name).trim().split(/\s+/).filter(Boolean)
      if (!parts.length) return '?'
      return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase()
    },
    avatarColor(name) {
      let hash = 0
      for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) % 997
      return AVATAR_COLORS[hash % AVATAR_COLORS.length]
    },
    loadWidth(load) {
      return `${Math.max((load / this.maxLoad) * 100, load > 0 ? 3 : 0)}%`
    },
    loadLevel(load) {
      if (load >= 100) return 'high'
      if (load >= 40) return 'mid'
      return 'low'
    },
  },
}
</script>

<style lang="scss" scoped>
$ink: #1d2b44;
$muted: #6b7a90;
$line: #d5e2f2;
$blue: #1c84f2;
$green: #13a98b;
$amber: #f0a73a;
$red: #e5484d;

.ua {
  width: 100%;
}

.ua-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 22px;
}

/* filters */
.ua-filters {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px 18px;
  margin-bottom: 18px;
  background: #eef4fb;
  border: 1px solid $line;
  border-radius: 16px;
  box-shadow: 0 2px 10px rgba(31, 45, 61, 0.04);

  &__row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
  }

  &__label {
    display: flex;
    align-items: center;
    gap: 5px;
    margin-right: 4px;
    font-size: 0.82rem;
    font-weight: 700;
    color: $blue;
  }
}

.ua-search {
  display: flex;
  align-items: center;
  flex: 1;
  gap: 8px;
  min-width: 260px;
  height: 42px;
  padding: 0 12px;
  color: $muted;
  background: #e3ecf8;
  border: 1px solid transparent;
  border-radius: 12px;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;

  &:focus-within {
    background: #f7faff;
    border-color: $blue;
    box-shadow: 0 0 0 3px rgba(10, 98, 232, 0.12);
  }

  input {
    flex: 1;
    border: 0;
    outline: none;
    background: transparent;
    color: $ink;
  }

  &__clear {
    display: flex;
    padding: 4px;
    color: $muted;
    background: transparent;
    border: 0;
    border-radius: 50%;
    cursor: pointer;

    &:hover {
      background: $line;
    }
  }
}

.ua-toggle,
.ua-reset {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 42px;
  padding: 0 16px;
  font-size: 0.84rem;
  font-weight: 600;
  color: #44546a;
  background: #eef4fb;
  border: 1px solid $line;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: $blue;
    color: $blue;
  }
}

.ua-toggle--active {
  color: #c93a3f;
  background: rgba(229, 72, 77, 0.1);
  border-color: rgba(229, 72, 77, 0.5);

  &:hover {
    color: #c93a3f;
    border-color: $red;
  }
}

.ua-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 13px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--accent);
  background: #eef4fb;
  border: 1px solid $line;
  border-radius: 99px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: var(--accent-soft);
    border-color: var(--accent);
  }

  &--active {
    color: #fff;
    background: var(--accent);
    border-color: var(--accent);

    &:hover {
      background: var(--accent);
    }
  }
}

/* table */
.ua-table-wrap {
  max-height: 66vh;
  overflow: auto;
  background: #eef4fb;
  border: 1px solid $line;
  border-radius: 16px;
  box-shadow: 0 2px 10px rgba(31, 45, 61, 0.04);

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c3cddc;
    border-radius: 99px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: $blue;
  }
}

.ua-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;

  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    padding: 14px 16px;
    font-size: 0.76rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    white-space: nowrap;
    color: #fff;
    background: linear-gradient(120deg, #0a62e8, #1c84f2);
    user-select: none;

    &.sortable {
      cursor: pointer;

      &:hover {
        background: linear-gradient(120deg, #0d6bf5, #2a90f7);
      }
    }

    &.is-sorted {
      background: linear-gradient(120deg, #084fc2, #0a62e8);
    }
  }

  .th-inner {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .th-arrow {
    display: inline-flex;
    padding: 2px;
    background: rgba(255, 255, 255, 0.22);
    border-radius: 6px;
  }

  tbody tr {
    transition: background 0.15s;

    &:nth-child(even) {
      background: #e9f1fa;
    }

    &:hover {
      background: #dde9f7;
    }
  }

  td {
    padding: 11px 16px;
    vertical-align: middle;
    border-bottom: 1px solid #d9e5f4;
  }

  .col-index {
    width: 56px;
    text-align: center;
    color: $muted;
    font-weight: 600;
  }

  .col-load {
    min-width: 170px;
  }

  .col-num {
    width: 110px;
  }
}

.ua-user {
  display: flex;
  align-items: center;
  gap: 12px;

  &__name {
    font-weight: 600;
    color: $ink;
  }
}

.ua-avatar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #fff;
  border-radius: 50%;
}

.ua-load {
  display: flex;
  align-items: center;
  gap: 10px;

  &__value {
    min-width: 34px;
    font-weight: 700;
    color: $ink;
  }

  &__track {
    flex: 1;
    height: 8px;
    overflow: hidden;
    background: #d9e5f4;
    border-radius: 99px;
  }

  &__bar {
    height: 100%;
    border-radius: 99px;
    transition: width 0.5s ease;

    &--low { background: $green; }
    &--mid { background: $amber; }
    &--high { background: $red; }
  }
}

.ua-num {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  font-size: 0.82rem;
  font-weight: 700;
  border-radius: 99px;

  &--pending {
    color: #a8700a;
    background: rgba(240, 167, 58, 0.14);
  }

  &--late {
    color: #c93a3f;
    background: rgba(229, 72, 77, 0.12);
  }

  &--zero {
    color: $muted;
    background: #e1eaf6;
  }
}

.ua-sources {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.ua-source {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 11px;
  font-size: 0.76rem;
  font-weight: 600;
  color: var(--accent);
  background: var(--accent-soft);
  border: 1px solid transparent;
  border-radius: 99px;
  cursor: pointer;
  transition: all 0.18s;

  &:hover {
    color: #fff;
    background: var(--accent);
    transform: translateY(-1px);
  }

  &__late {
    position: absolute;
    top: -2px;
    right: -2px;
    width: 9px;
    height: 9px;
    background: $red;
    border: 2px solid #eef4fb;
    border-radius: 50%;
  }
}

.ua-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 0;
  color: $muted;
  background: #eef4fb;
  border: 1px dashed $line;
  border-radius: 16px;
}

.ua-footnote {
  margin: 10px 4px 0;
  font-size: 0.78rem;
  color: $muted;
}
</style>
